import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = "/api";

/**
 * GuestPage - Gast-Meldungs-Formular
 * 
 * KOMPLETT ISOLIERT - KEINE React-Router-Hooks
 */
const GuestPage = () => {
  // Property-ID aus URL manuell extrahieren (NICHT useParams!)
  const getPropertyIdFromUrl = () => {
    const path = window.location.pathname;
    const match = path.match(/\/guest\/([^/]+)/);
    return match ? match[1] : null;
  };
  
  const [propertyId] = useState(getPropertyIdFromUrl);
  const fileInputRef = useRef(null);
  
  const [pageState, setPageState] = useState('loading'); // loading | form | submitting | success | error
  const [property, setProperty] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'reparatur',
    priority: 'normal',
    reporter_name: '',
    reporter_email: ''
  });

  // Property laden
  useEffect(() => {
    const loadProperty = async () => {
      try {
        const response = await axios.get(`${API}/guest/property/${propertyId}`);
        setProperty(response.data);
        setPageState('form');
      } catch (err) {
        setErrorMessage('Das Objekt konnte nicht gefunden werden.');
        setPageState('error');
      }
    };
    
    loadProperty();
  }, [propertyId]);

  // Bild hinzufügen
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setUploadedImages(prev => [...prev, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Bild entfernen
  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageState('submitting');
    setErrorMessage('');
    
    try {
      const response = await axios.post(`${API}/guest/tasks/${propertyId}`, formData);
      const taskId = response.data.id;
      
      // Bilder hochladen
      if (uploadedImages.length > 0) {
        for (const img of uploadedImages) {
          const imgFormData = new FormData();
          imgFormData.append('file', img.file);
          imgFormData.append('task_id', taskId);
          
          try {
            await axios.post(`${API}/guest/tasks/${propertyId}/images`, imgFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (imgError) {
            console.error('Image upload error:', imgError);
          }
        }
      }
      
      setPageState('success');
      
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || 'Ein Fehler ist aufgetreten.');
      setPageState('form');
    }
  };

  // Formular zurücksetzen
  const resetForm = () => {
    setPageState('form');
    setUploadedImages([]);
    setFormData({
      title: '',
      description: '',
      category: 'reparatur',
      priority: 'normal',
      reporter_name: '',
      reporter_email: ''
    });
  };

  // Styles
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '32px 16px',
      fontFamily: 'Arial, sans-serif'
    },
    container: {
      maxWidth: '480px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginBottom: '16px'
    },
    cardContent: {
      padding: '24px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px'
    },
    logo: {
      width: '64px',
      height: '64px',
      backgroundColor: '#0E4C92',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px auto',
      color: '#ffffff',
      fontWeight: '700',
      fontSize: '20px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: '14px',
      color: '#64748b',
      margin: '0'
    },
    propertyCard: {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px'
    },
    propertyIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: '#f1f5f9',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    propertyName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e293b',
      margin: '0 0 4px 0'
    },
    propertyAddress: {
      fontSize: '14px',
      color: '#64748b',
      margin: '0'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px'
    },
    input: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      padding: '0 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      boxSizing: 'border-box',
      marginBottom: '16px'
    },
    textarea: {
      width: '100%',
      minHeight: '120px',
      fontSize: '16px',
      padding: '12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      boxSizing: 'border-box',
      marginBottom: '16px',
      resize: 'vertical',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      padding: '0 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: '#f9fafb',
      marginBottom: '16px'
    },
    button: {
      width: '100%',
      height: '52px',
      backgroundColor: '#0E4C92',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed'
    },
    errorBox: {
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px'
    },
    errorText: {
      color: '#b91c1c',
      fontSize: '14px',
      margin: '0'
    },
    successIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#d1fae5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px auto',
      fontSize: '40px'
    },
    successTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      textAlign: 'center',
      marginBottom: '16px'
    },
    successText: {
      fontSize: '16px',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: '24px'
    },
    imageGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '16px'
    },
    imagePreview: {
      width: '80px',
      height: '80px',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative',
      border: '1px solid #e5e7eb'
    },
    imageRemove: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      width: '20px',
      height: '20px',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    addImageBtn: {
      width: '80px',
      height: '80px',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: '#9ca3af',
      backgroundColor: 'transparent'
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '3px solid #e5e7eb',
      borderTopColor: '#0E4C92',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  // LOADING
  if (pageState === 'loading') {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: '#64748b', marginTop: '16px' }}>Lade...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ERROR
  if (pageState === 'error' && !property) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ ...styles.cardContent, textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                Objekt nicht gefunden
              </h2>
              <p style={{ color: '#64748b', margin: '0' }}>
                Der QR-Code ist ungültig oder das Objekt existiert nicht mehr.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS
  if (pageState === 'success') {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={{ ...styles.cardContent, textAlign: 'center', padding: '48px 24px' }}>
              <div style={styles.successIcon}>✓</div>
              <h2 style={styles.successTitle}>Vielen Dank!</h2>
              <p style={styles.successText}>
                Ihre Meldung wurde erfolgreich übermittelt. Wir kümmern uns darum.
              </p>
              <button
                onClick={resetForm}
                style={styles.button}
                data-testid="guest-new-report-btn"
              >
                Weitere Meldung erstellen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FORM
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logo}>GB</div>
          <h1 style={styles.title}>Meldung erstellen</h1>
          <p style={styles.subtitle}>Schäden, Hinweise oder Anfragen melden</p>
        </div>

        {/* Property Info */}
        <div style={styles.propertyCard}>
          <div style={styles.propertyIcon}>🏠</div>
          <div>
            <p style={styles.propertyName}>{property?.name}</p>
            <p style={styles.propertyAddress}>📍 {property?.address}</p>
          </div>
        </div>

        {/* Form */}
        <div style={styles.card}>
          <div style={styles.cardContent}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
              Ihre Meldung
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
              Bitte beschreiben Sie Ihr Anliegen möglichst genau
            </p>
            
            {errorMessage && (
              <div style={styles.errorBox}>
                <p style={styles.errorText}>{errorMessage}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <label style={styles.label}>Titel / Kurzbeschreibung *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="z.B. Wasserhahn tropft"
                required
                disabled={pageState === 'submitting'}
                style={styles.input}
                data-testid="guest-title-input"
              />

              <label style={styles.label}>Beschreibung *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Beschreiben Sie das Problem oder Ihr Anliegen ausführlich..."
                required
                disabled={pageState === 'submitting'}
                style={styles.textarea}
                data-testid="guest-description-input"
              />

              {/* Fotos */}
              <label style={styles.label}>Fotos hinzufügen (optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                data-testid="guest-image-input"
              />
              <div style={styles.imageGrid}>
                {uploadedImages.map((img, index) => (
                  <div key={index} style={styles.imagePreview}>
                    <img
                      src={img.preview}
                      alt={img.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      style={styles.imageRemove}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={styles.addImageBtn}
                  data-testid="guest-add-image-btn"
                >
                  📷
                  <span style={{ fontSize: '10px', marginTop: '4px' }}>Foto</span>
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '16px' }}>
                Tippen Sie auf "Foto", um mit der Kamera aufzunehmen
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={styles.label}>Kategorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    disabled={pageState === 'submitting'}
                    style={{ ...styles.select, marginBottom: '0' }}
                    data-testid="guest-category-select"
                  >
                    <option value="eigentuemer">Für Eigentümer</option>
                    <option value="reinigung">Reinigung</option>
                    <option value="reparatur">Reparatur</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Dringlichkeit</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    disabled={pageState === 'submitting'}
                    style={{ ...styles.select, marginBottom: '0' }}
                    data-testid="guest-priority-select"
                  >
                    <option value="normal">Normal</option>
                    <option value="hoch">Dringend</option>
                  </select>
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
                  Optional: Ihre Kontaktdaten für Rückfragen
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={styles.label}>Ihr Name</label>
                    <input
                      type="text"
                      value={formData.reporter_name}
                      onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                      placeholder="Max Mustermann"
                      disabled={pageState === 'submitting'}
                      style={{ ...styles.input, marginBottom: '0' }}
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div>
                    <label style={styles.label}>Ihre E-Mail</label>
                    <input
                      type="email"
                      value={formData.reporter_email}
                      onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
                      placeholder="max@example.de"
                      disabled={pageState === 'submitting'}
                      style={{ ...styles.input, marginBottom: '0' }}
                      data-testid="guest-email-input"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={pageState === 'submitting'}
                style={{
                  ...styles.button,
                  ...(pageState === 'submitting' ? styles.buttonDisabled : {}),
                  marginTop: '24px'
                }}
                data-testid="guest-submit-btn"
              >
                {pageState === 'submitting' ? '⏳ Wird gesendet...' : '📤 Meldung absenden'}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
          GB Objekt- & Aufgabenmanagement • Sylt
        </p>
        
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default GuestPage;
